import { ImagePlus, RefreshCw, Trash2 } from "lucide-react"
import { useId, useRef, useState } from "react"
import { cn } from "@/shared/lib/utils"
import { Button } from "@/shared/ui/button"

/**
 * Обложка материала в каталоге приложений. Картинку можно бросить мышью или выбрать.
 * Показ идёт в пропорции 16:9, как в карточке каталога.
 */
export function CoverField({
	url,
	onPick,
	onRemove,
	busy,
}: {
	url: string | null
	onPick: (file: File) => void
	/** Убрать выбранную или загруженную обложку. */
	onRemove?: () => void
	busy?: boolean
}) {
	const inputId = useId()
	const input = useRef<HTMLInputElement>(null)
	const [dragging, setDragging] = useState(false)

	const take = (files: FileList | null) => {
		const file = files?.[0]
		if (file?.type.startsWith("image/")) onPick(file)
	}

	return (
		<div>
			<input
				ref={input}
				id={inputId}
				type="file"
				accept="image/jpeg,image/png,image/webp"
				className="sr-only"
				onChange={(event) => {
					take(event.target.files)
					event.target.value = ""
				}}
			/>
			<div
				onDragOver={(event) => {
					event.preventDefault()
					setDragging(true)
				}}
				onDragLeave={() => setDragging(false)}
				onDrop={(event) => {
					event.preventDefault()
					setDragging(false)
					take(event.dataTransfer.files)
				}}
				className={cn(
					"group relative aspect-video overflow-hidden rounded-xl border border-dashed transition-colors",
					url ? "border-transparent" : "border-border bg-background/40",
					dragging && "border-primary bg-primary/5",
				)}
			>
				{url ? (
					<>
						<img src={url} alt="Обложка материала" className="size-full object-cover" />
						<div className="absolute inset-x-0 bottom-0 flex justify-end gap-2 bg-gradient-to-t from-black/70 to-transparent p-3 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
							<Button
								type="button"
								size="sm"
								variant="secondary"
								disabled={busy}
								onClick={() => input.current?.click()}
							>
								<RefreshCw />
								Заменить
							</Button>
							{onRemove ? (
								<Button
									type="button"
									size="sm"
									variant="secondary"
									disabled={busy}
									onClick={onRemove}
									aria-label="Убрать обложку"
								>
									<Trash2 />
								</Button>
							) : null}
						</div>
					</>
				) : (
					<label
						htmlFor={inputId}
						className="flex size-full cursor-pointer flex-col items-center justify-center gap-2 text-center text-sm text-muted-foreground transition-colors hover:text-foreground"
					>
						<span className="grid size-11 place-items-center rounded-full bg-secondary">
							<ImagePlus className="size-5" />
						</span>
						<span>
							<span className="font-medium text-foreground">Выберите картинку</span> или перетащите
							сюда
						</span>
						<span className="text-xs">JPEG, PNG или WebP, лучше 1280×720</span>
					</label>
				)}
			</div>
		</div>
	)
}
